// EL REPASO OBLIGATORIO AL ENTRAR.
//
// El día del repaso, y solo ese día, quien entra en la app se encuentra la lista de urgencias
// delante: recepción con todos los pacientes, cada doctor con los suyos. Mientras quede alguien sin
// decidir, el modal vuelve y el badge del menú sigue puesto.
//
// SE PUEDE POSPONER, Y ES DELIBERADO. Bloquear del todo a alguien que entra corriendo a atender una
// urgencia sería exactamente el fallo que este módulo intenta evitar. "Ahora no" lo aparta un rato
// (no lo cancela): el aplazamiento se guarda por USUARIO y por CICLO, así que ni se hereda entre
// personas que comparten ordenador ni sobrevive al repaso de la semana que viene.
//
// Se monta en el Layout, al lado de AvisoAdminBanner (el mecanismo que ya existe para "algo que
// tienes que ver al entrar"), en vez de inventar otro sitio nuevo desde el que interrumpir.
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { usePermisos } from '../hooks/usePermisos'
import { Boton, Ico } from './ui'
import { TablaRepaso, useRepaso, useRepasoEstado, fechaLarga } from './RepasoUrgencias'
import { t as i18nT, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Cuánto se aparta con "Ahora no". Dos horas: lo bastante para atender lo que te ha traído a la app
// y lo bastante poco para que el repaso se haga HOY, que es todo el sentido de que exista.
const APLAZAR_MS = 2 * 60 * 60 * 1000
const clave = (userId) => `urgRepasoAplazado:${userId || 'anon'}`

function aplazadoHasta(userId, ciclo) {
  try {
    const v = JSON.parse(localStorage.getItem(clave(userId)) || 'null')
    // El aplazamiento es DE ESTE CICLO: uno guardado la semana pasada no puede tapar el de hoy.
    if (!v || v.ciclo !== ciclo) return 0
    return Number(v.hasta) || 0
  } catch (_) { return 0 }
}

export default function RepasoUrgenciasModal() {
  const { user } = useAuth()
  const navigate = useNavigate()
  // Solo se pregunta por el repaso a quien puede tener uno. Sin esto, cada usuario de la clínica
  // (almacén, limpieza, el tótem de fichar) haría una consulta por minuto para recibir siempre un
  // cero — y ahora, además, un 403.
  //
  // Se mira el PERMISO 'urgencias', no una lista de roles copiada aquí: el acceso se concede por rol
  // Y por usuario, así que una lista de roles dejaría fuera del repaso justo a la persona a la que un
  // administrador se lo acaba de dar. (Que además PUEDA decidir lo decide el servidor: sin
  // 'urgencias_decidir' no manda `obligatorio` y el modal no sale, porque serían dos botones
  // apagados delante de alguien que no puede resolverlo.)
  const { perms } = usePermisos()
  const leToca = perms?.urgencias === true
  const { data: estado } = useRepasoEstado(!!user && leToca)
  // La lista completa solo se pide cuando de verdad va a salir el modal: es una consulta con
  // subconsultas por paciente y no tiene por qué correr en cada carga de cada pantalla.
  const toca = !!(estado && estado.obligatorio)
  const { data } = useRepaso({ enabled: toca })
  const [ahora, setAhora] = useState(() => Date.now())

  // Reloj perezoso: solo hace falta para saber si el aplazamiento ya ha vencido. Un minuto es de
  // sobra y no repinta nada más. (new Date() aquí es un RELOJ, no una fecha de la base de datos.)
  useEffect(() => {
    if (!toca) return
    const t = setInterval(() => setAhora(Date.now()), 60000)
    return () => clearInterval(t)
  }, [toca])

  if (!user || !toca) return null
  const ciclo = estado.ciclo
  if (ahora < aplazadoHasta(user.id, ciclo)) return null

  const aplazar = () => {
    try { localStorage.setItem(clave(user.id), JSON.stringify({ ciclo, hasta: Date.now() + APLAZAR_MS })) } catch (_) {}
    setAhora(Date.now())
  }

  const pendientes = estado.pendientes || 0

  return (
    <div className="modal-safe" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2400,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
        boxShadow: '0 24px 60px rgba(0,0,0,0.3)', width: 1100, maxWidth: '100%',
        maxHeight: 'calc(var(--alto-pantalla) - 40px)', display: 'flex', flexDirection: 'column',
      }}>
        <header style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px',
          borderBottom: '1px solid var(--border)', background: 'var(--surface)',
          borderTopLeftRadius: 'var(--radius-lg)', borderTopRightRadius: 'var(--radius-lg)',
        }}>
          <span style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center', color: 'var(--danger)',
            background: 'color-mix(in srgb, var(--danger) 14%, transparent)',
          }}><Ico n="bell" s={21} /></span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>
              {i18nT('components.RepasoUrgenciasModal.repaso_de_la_lista_de', 'Repaso de la lista de urgencias')}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              {i18nT('components.RepasoUrgenciasModal.hoy_toca_repasar_quien_puede', 'Hoy toca repasar quién puede escribir cuando la clínica está cerrada. Quedan ')}<b>{pendientes}</b>{tx('components.RepasoUrgenciasModal.por_decidir', ' por decidir{{x}}.', { x: ciclo ? i18nT('components.RepasoUrgenciasModal.repaso_del', ' · repaso del {{fechaLarga}}', { fechaLarga: fechaLarga(ciclo) }) : '' })}
            </div>
          </div>
        </header>

        <div style={{ padding: '12px 16px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {i18nT('components.RepasoUrgenciasModal.de_cada_paciente', 'De cada paciente: ')}<b>{i18nT('components.RepasoUrgenciasModal.sigue_en_urgencias', 'Sigue en urgencias')}</b>{i18nT('components.RepasoUrgenciasModal.si_mantiene_el_permiso_para', ' si mantiene el permiso para escribir el fin de semana, o ')}<b>{i18nT('components.RepasoUrgenciasModal.quitar_de_urgencias', 'Quitar de urgencias')}</b>{i18nT('components.RepasoUrgenciasModal.si_ya_no_hace_falta', ' si ya no hace falta. Cuando no quede ninguno, esta ventana deja de salir.')}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 12px' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            {/* Mientras carga NO se pinta la tabla vacía: diría "no hay nadie en la lista" en una
                ventana que acaba de decir que quedan pacientes por decidir. */}
            {data
              ? <TablaRepaso data={data} compacto />
              : <div style={{ padding: 22, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{i18nT('components.RepasoUrgenciasModal.cargando_la_lista', 'Cargando la lista…')}</div>}
          </div>
        </div>

        <footer style={{
          display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 20px', borderTop: '1px solid var(--border)', background: 'var(--surface)',
          borderBottomLeftRadius: 'var(--radius-lg)', borderBottomRightRadius: 'var(--radius-lg)', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            {i18nT('components.RepasoUrgenciasModal.si_ahora_no_puedes_volvera', 'Si ahora no puedes, volverá a salir en un par de horas. El aviso del menú no se va hasta que la lista esté repasada.')}
          </span>
          <span style={{ display: 'flex', gap: 9 }}>
            <Boton onClick={aplazar}>{i18nT('components.RepasoUrgenciasModal.ahora_no', 'Ahora no')}</Boton>
            <Boton variante="primario" icon="clipboard" onClick={() => { aplazar(); navigate('/urgencias') }}>
              {i18nT('components.RepasoUrgenciasModal.abrir_la_pantalla_de_urgencias', 'Abrir la pantalla de Urgencias')}
            </Boton>
          </span>
        </footer>
      </div>
    </div>
  )
}
